import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {WorkOrderService} from '../../../Services/work-order.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {DataService} from '../../../Services/data.service';
import {TransactionEntityService} from '../../../Services/transaction-entity.service';
import {WoType, WoCategory, WoAsset} from '../../../DataModels/workorder-model';
import {WoNotificationsTabComponent} from '../tabs/wo-notifications-tab/wo-notifications-tab.component';
import {PrintBuilderService} from '../../../Services/print-builder.service';
import {EmailModel} from '../../../DataModels/email-model';

@Component({
  selector: 'app-work-orders-form',
  templateUrl: './work-orders-form.component.html',
  styleUrls: ['./work-orders-form.component.css'],
  standalone: false,
})
export class WorkOrdersFormComponent implements OnInit {

  @ViewChild('notificationsTab') notificationsTab?: WoNotificationsTabComponent;

  workorderId: string | null = null;
  printVm: any = null;
  reportDataToSendInEmail: EmailModel = new EmailModel();
  woNumber: string = '';
  isLoaded = false;
  saving = false;
  activeTab = 'hours';
  techEmailAutoSent = false;
  invoiceId: string | null = null;

  form: UntypedFormGroup;

  // Lookup lists
  woTypes: WoType[] = [];
  woCategories: WoCategory[] = [];
  checklistTemplates: any[] = [];
  assets: WoAsset[] = [];
  entities: any[] = [];
  users: any[] = [];
  locations: any[] = [];
  addresses: any[] = [];
  contacts: any[] = [];
  companies: any[] = [];

  // Status options
  statusOptions = ['Open', 'Dispatched', 'In Progress', 'Completed', 'Invoiced'];
  priorityOptions = ['Critical', 'High', 'Normal', 'Low'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private fb: UntypedFormBuilder,
    private titleService: Title,
    private woService: WorkOrderService,
    private dataService: DataService,
    private entityService: TransactionEntityService,
    private notification: BsToastService,
    public confirmDialog: ConfirmDialogService,
    private printBuilder: PrintBuilderService,
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.titleService.setTitle('Work Order');
    this.workorderId = this.route.snapshot.queryParamMap.get('id')
      ?? this.route.snapshot.queryParamMap.get('workorderId');
    this.loadLookups();
    if (this.workorderId) {
      this.loadWorkOrder();
    } else {
      this.isLoaded = true;
    }
  }

  private buildForm(): UntypedFormGroup {
    return this.fb.group({
      // Core
      company_id: [null, Validators.required],
      entity_id: [null, Validators.required],
      address_id: [null],
      contact_id: [null],
      location_id: [null],
      assigned_user_id: [null],
      status: ['Open'],
      priority: ['Normal'],
      work_order_date: [new Date().toISOString().substring(0, 10), Validators.required],
      required_by_date: [null],
      next_visit_date: [null],
      completed_date: [null],
      description: ['', Validators.required],
      dispatch_notes: [''],
      // Refactor fields
      type_id: [null],
      category_id: [null],
      checklist_id: [null],
      asset_id: [null],
      estimated_hours: [null],
      estimated_cost: [null],
      billing_status: [{value: 'unbilled', disabled: true}],
      travel_unit_type: ['miles'],
      sla_response_hours: [null],
      sla_resolution_hours: [null],
      notify_on_dispatch: [false],
      notify_on_arrival: [false],
      notify_on_completion: [false],
      notification_email: [''],
      internal_notes: [''],
      customer_visible_notes: [''],
    });
  }

  private loadLookups(): void {
    this.dataService.getDataWithParams('company', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => this.companies = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    this.woService.getWoTypes().subscribe({
      next: (r: any) => this.woTypes = r?.Data ?? r?.data ?? r ?? [], error: () => {
      }
    });
    this.woService.getWoCategories().subscribe({
      next: (r: any) => this.woCategories = r?.Data ?? r?.data ?? r ?? [], error: () => {
      }
    });
    this.woService.getChecklistTemplates().subscribe({
      next: (r: any) => this.checklistTemplates = r?.Data ?? r?.data ?? r ?? [], error: () => {
      }
    });
    this.entityService.loadEntities().subscribe({
      next: (r: any) => this.entities = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    this.dataService.getDataWithParams('sys_user', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => this.users = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    this.dataService.getDataWithParams('location', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => this.locations = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    this.woService.getAssets().subscribe({
      next: (r: any) => this.assets = r?.Data ?? r?.data ?? r ?? [], error: () => {
      }
    });
  }

  private loadWorkOrder(): void {
    this.woService.getWorkOrder(this.workorderId!).subscribe({
      next: (res: any) => {
        console.log('[WO Load] GET api/WorkOrder/' + this.workorderId, res);
        const h = res?.wo_header ?? res;
        this.woNumber = h.number ?? '';
        this.form.patchValue({
          company_id: h.company_id ?? null,
          entity_id: h.entity_id,
          address_id: h.address_id,
          contact_id: h.contact_id,
          location_id: h.location_id,
          assigned_user_id: h.assigned_user_id,
          status: h.status,
          priority: h.priority,
          work_order_date: h.work_order_date?.substring(0, 10),
          required_by_date: h.required_by_date?.substring(0, 10) ?? null,
          next_visit_date: h.next_visit_date?.substring(0, 10) ?? null,
          completed_date: h.completed_date?.substring(0, 10) ?? null,
          description: h.description,
          dispatch_notes: h.dispatch_notes,
          type_id: h.type_id ?? null,
          category_id: h.category_id ?? null,
          checklist_id: h.checklist_id ?? null,
          asset_id: h.asset_id ?? null,
          estimated_hours: h.estimated_hours ?? null,
          estimated_cost: h.estimated_cost ?? null,
          billing_status: h.billing_status ?? 'unbilled',
          travel_unit_type: h.travel_unit_type ?? 'miles',
          sla_response_hours: h.sla_response_hours ?? null,
          sla_resolution_hours: h.sla_resolution_hours ?? null,
          notify_on_dispatch: h.notify_on_dispatch ?? false,
          notify_on_arrival: h.notify_on_arrival ?? false,
          notify_on_completion: h.notify_on_completion ?? false,
          notification_email: h.notification_email ?? '',
          internal_notes: h.internal_notes ?? '',
          customer_visible_notes: h.customer_visible_notes ?? '',
        });
        this.invoiceId = res.isInvoiced ?? null;
        this.printVm = res;
        // Load addresses/contacts for selected entity
        if (h.entity_id) {
          this.onEntityChange(h.entity_id);
        }
        this.isLoaded = true;
      },
      error: () => {
        this.notification.showErrorToast('Error loading work order.');
        this.isLoaded = true;
      }
    });
  }

  onEntityChange(entityId: string): void {
    if (!entityId) {
      this.addresses = [];
      this.contacts = [];
      return;
    }
    this.entityService.loadEntityAddresses(entityId).subscribe({
      next: (r: any) => this.addresses = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    this.entityService.loadEntityContacts(entityId).subscribe({
      next: (r: any) => this.contacts = r?.Data ?? r?.data ?? r ?? [],
      error: () => {
      }
    });
    // Filter assets by entity
    this.woService.getAssets({entity_id: entityId}).subscribe({
      next: (r: any) => this.assets = r?.Data ?? r?.data ?? r ?? [], error: () => {
      }
    });
  }

  get selectedEntityEmail(): string {
    const entityId = this.form.get('entity_id')?.value;
    if (!entityId) {
      return '';
    }
    return this.entities.find(e => e.id === entityId)?.email ?? '';
  }

  get selectedEntity(): any {
    const entityId = this.form.get('entity_id')?.value;
    if (!entityId) {
      return null;
    }
    return this.entities.find(e => e.id === entityId) ?? null;
  }

  get selectedAddress(): any {
    const addressId = this.form.get('address_id')?.value;
    if (!addressId) {
      return null;
    }
    return this.addresses.find((a: any) => a.id === addressId) ?? null;
  }

  get selectedContact(): any {
    const contactId = this.form.get('contact_id')?.value;
    if (!contactId) {
      return null;
    }
    return this.contacts.find((c: any) => c.id === contactId) ?? null;
  }

  onTypeChange(typeId: string): void {
    const type = this.woTypes.find(t => t.id === typeId);
    if (type) {
      if (type.default_sla_response_hours && !this.form.get('sla_response_hours')?.value) {
        this.form.patchValue({sla_response_hours: type.default_sla_response_hours});
      }
      if (type.default_sla_resolution_hours && !this.form.get('sla_resolution_hours')?.value) {
        this.form.patchValue({sla_resolution_hours: type.default_sla_resolution_hours});
      }
    }
  }

  save(): void {
    if (this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notification.showWarningToast('Please fill in all required fields.');
      return;
    }
    this.saving = true;
    const raw = this.form.getRawValue();

    // Wrap header fields under wo_header per API contract; omit child arrays
    const body: any = {wo_header: raw};

    if (this.workorderId) {
      console.log('[WO Save] PATCH api/WorkOrder/' + this.workorderId, body);
      this.woService.updateWorkOrder(this.workorderId, body).subscribe({
        next: () => {
          this.saving = false;
        },
        error: () => {
          this.saving = false;
        }
      });
    } else {
      console.log('[WO Save] POST api/WorkOrder', body);
      this.woService.createWorkOrder(body).subscribe({
        next: (res: any) => {
          this.saving = false;
          const newId = res?.id ?? res?.wo_header?.id;
          if (newId) {
            this.workorderId = newId;
            const url = this.router.createUrlTree([], {
              relativeTo: this.route,
              queryParams: {id: newId},
              queryParamsHandling: 'merge',
            }).toString();
            this.location.replaceState(url);
          }
        },
        error: () => {
          this.saving = false;
        }
      });
    }
  }

  deleteItem(): void {
    if (!this.workorderId) {
      return;
    }
    this.confirmDialog.confirmThis(
      ['Delete this work order? This cannot be undone.'],
      () => {
        this.woService.deleteWorkOrder(this.workorderId!).subscribe({
          next: () => {
            this.notification.showSuccessToast('Work order deleted.');
            this.router.navigate(['/work-orders/work-orders-list']);
          },
          error: () => {
            this.notification.showErrorToast('Error deleting work order.');
          }
        });
      },
      () => {
      }
    );
  }

  // ── Lifecycle transitions ──────────────────────────────────────────────────

  dispatch(): void {
    if (!this.workorderId) {
      return;
    }
    this.woService.dispatch(this.workorderId).subscribe({
      next: (res: any) => {
        this.notification.showSuccessToast('Work order dispatched.');
        this.form.patchValue({status: res.status ?? 'Dispatched'});
        this.techEmailAutoSent = true;
        this.activeTab = 'notifications';
        setTimeout(() => this.notificationsTab?.notifyTech(), 300);
        setTimeout(() => this.notificationsTab?.openNotifyModal(), 300);
        setTimeout(() => this.notificationsTab?.notifyCustomer('dispatched'), 300);
      },
      error: () => {
        this.notification.showErrorToast('Error dispatching work order.');
      }
    });
  }

  reNotify(): void {
    this.notificationsTab?.openNotifyModal();
  }

  arrive(): void {
    if (!this.workorderId) {
      return;
    }
    this.woService.arrive(this.workorderId).subscribe({
      next: (res: any) => {
        this.notification.showSuccessToast('Arrival recorded.');
        this.form.patchValue({status: res.status ?? 'In Progress'});
      },
      error: () => {
        this.notification.showErrorToast('Error recording arrival.');
      }
    });
  }

  complete(): void {
    if (!this.workorderId) {
      return;
    }
    this.woService.complete(this.workorderId).subscribe({
      next: (res) => {
        if (res.checklist_incomplete) {
          this.notification.showWarningToast('Work order completed with incomplete checklist items.');
        } else {
          this.notification.showSuccessToast('Work order completed.');
        }
        this.form.patchValue({status: res.status ?? 'Completed'});
        setTimeout(() => this.notificationsTab?.notifyCustomer('completed'), 300);
      },
      error: () => {
        this.notification.showErrorToast('Error completing work order.');
      }
    });
  }


  goToInvoice(): void {
    if (this.invoiceId) {
      this.router.navigate(['/transactions/invoice/ar/form'], {queryParams: {documentId: this.invoiceId}});
      ///transactions/invoice/ar/form?invoiceId=bf5b2d11-f87d-467b-a7e6-a98ac37ce79d
    }
  }

  onClose(): void {
    this.router.navigate(['/work-orders/work-orders-list']);
  }

  onInvoiceCreated(): void {
    if (!this.workorderId) {
      return;
    }
    this.woService.getWorkOrder(this.workorderId).subscribe({
      next: (res: any) => {
        const h = res?.wo_header ?? res;
        this.form.get('billing_status')?.setValue(h.billing_status ?? 'unbilled');
        this.form.get('status')?.setValue(h.status ?? this.currentStatus);
        this.invoiceId = res.isInvoiced ?? null;
        this.notificationsTab?.load();
      },
      error: () => {
      }
    });
  }

  onInvoiceDeleted(newBillingStatus: string): void {
    this.form.get('billing_status')?.setValue(newBillingStatus);
    this.invoiceId = null;
    this.activeTab = 'billing';
    this.notificationsTab?.load();
  }

  onDeleteInvoiceFromHeader(): void {
    if (!this.workorderId || !this.invoiceId) {
      this.notification.showErrorToast('No invoice found to delete.');
      return;
    }
    this.confirmDialog.confirmThis(
      ['Delete this invoice?', 'This cannot be undone.'],
      () => {
        this.woService.deleteInvoice(this.workorderId!, this.invoiceId!).subscribe({
          next: (res) => {
            this.notification.showSuccessToast(`Invoice ${res.invoice_number} deleted.`);
            this.form.get('billing_status')?.setValue(res.billing_status);
            this.invoiceId = null;
            this.activeTab = 'billing';
            this.notificationsTab?.load();
          },
          error: (err) => {
            const msg = err?.error?.error ?? err?.error?.message ?? 'Error deleting invoice.';
            this.notification.showErrorToast(msg);
          }
        });
      },
      () => {
      }
    );
  }

  private buildPrintVm(): any {
    const companyId = this.form.get('company_id')?.value;
    return {
      item: this.printVm,
      company: this.companies.find((c: any) => c.id === companyId) ?? null,
      user: this.users,
      entity: this.selectedEntity,
      address: this.selectedAddress,
      contact: this.selectedContact,
      woTypes: this.woTypes,
      woCategories: this.woCategories,
    };
  }

  printDocument(): void {
    if (!this.printVm) {
      return;
    }
    try {
      const html = this.printBuilder.buildPrintHtml(this.buildPrintVm(), 'workorder');
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      const cleanup = () => setTimeout(() => {
        try {
          iframe.remove();
        } catch { /* ignore */
        }
      }, 400);
      const doPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch { /* ignore */
        } finally {
          cleanup();
        }
      };
      iframe.onload = () => {
        try {
          doPrint();
        } catch {
          doPrint();
        }
      };
      (iframe as any).srcdoc = html;
      document.body.appendChild(iframe);
    } catch (e) {
      console.error('[WO] printDocument failed:', e);
    }
  }

  emailDocument(): void {
    if (!this.printVm) {
      return;
    }
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = this.workorderId ?? '';
    emailDocObj.toEmail = this.selectedEntityEmail;
    emailDocObj.body = 'Please see the attached Work Order - ' + this.woNumber + '.<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp Work Order - ' + this.woNumber;
    emailDocObj.activity_message = 'Work Order';
    try {
      emailDocObj.pdfHtml = this.printBuilder.buildEmailHtml(this.buildPrintVm(), 'workorder');
    } catch {
      emailDocObj.pdfHtml = '';
    }
    emailDocObj.FileName = `Work_Order_${this.woNumber}.pdf`;
    this.reportDataToSendInEmail = {...emailDocObj};
  }

  get currentStatus(): string {
    return this.form.get('status')?.value ?? 'Open';
  }

  get billingStatus(): string {
    return (this.form.get('billing_status')?.value ?? 'unbilled').replace(/_/g, ' ');
  }

  billingStatusClass(): string {
    switch (this.billingStatus) {
      case 'billed':
        return 'bg-success';
      case 'partially billed':
        return 'bg-info';
      case 'no charge':
        return 'bg-secondary';
      default:
        return 'bg-warning text-dark';
    }
  }

  priorityClass(): string {
    switch (this.form.get('priority')?.value) {
      case 'Critical':
        return 'bg-danger';
      case 'High':
        return 'bg-warning text-dark';
      case 'Low':
        return 'bg-secondary';
      default:
        return 'bg-primary';
    }
  }
}
